import {
    buildReceiptAllocationRows,
    buildReceiptContentRows,
} from './receiptDetailHelpers';

/** Dựng các dòng edit từ projection tài chính của chính phiếu, không lấy tổng của phiếu khác. */
export const buildEditableReceiptItems = (receipt = {}) => {
    const contentRows = buildReceiptContentRows(
        receipt.items || [],
        buildReceiptAllocationRows(receipt.financial_lineage || {}),
        receipt.service_allocation_summary || []
    );

    return contentRows.map(item => ({
        key: `${item.type}-${item.id}`,
        id: item.id,
        receipt_item_id: item.receipt_id ? item.id : item.receipt_item_id,
        origin_id: item.origin_id,
        product_sale_id: item.product_sale_id,
        name: item.name,
        price: item.price,
        quantity: item.quantity,
        line_total: Number(item.price || 0) * Number(item.quantity || 0),
        amount: item.type === 'Sản phẩm'
            ? Number(item.quantity || 0) * Number(item.price || 0)
            : item.amount,
        amount_to_pay: item.amount_to_pay,
        // Khi sửa phiếu, số cần thu là công nợ ngay trước phiếu đã được backend snapshot.
        // Không thay item.amount vì đó là nội dung lịch sử phải giữ nguyên khi PUT không đổi tài chính.
        outstanding_amount: item.type !== 'Sản phẩm' && item.debt_before_amount != null
            ? Number(item.debt_before_amount)
            : item.outstanding_amount,
        // Đây là số tiền thuộc riêng phiếu đang sửa; không cộng current_service_financial.
        allocated_amount: Number(item.receipt_paid_allocated_amount || 0),
        effective_allocated_amount: Number(item.receipt_paid_effective_amount || 0),
        refunded_allocated_amount: Number(item.receipt_paid_refunded_amount || 0),
        allocation_scope: 'RECEIPT',
        type: item.type,
        customer_service_id: item.customer_service_id,
        selected_at: item.selected_at || item.current_service_financial?.selected_at || null,
    })).filter((item, index, allItems) => (
        allItems.findIndex(other => other.key === item.key) === index
    ));
};

export const buildReceiptMutationConfirmationSummary = preview => ({
    changedCount: (preview?.removed_items || []).length
        + (preview?.changed_items || []).length + (preview?.added_items || []).length,
    productReversal: Number(preview?.product_payment_to_reverse || 0),
    serviceReversal: Number(preview?.service_allocation_to_reverse || 0),
    depositReversal: Number(preview?.deposit_allocation_to_reverse || 0),
    balanceReversal: Number(preview?.balance_allocation_to_reverse || 0),
    completedRefundAmount: Number(preview?.completed_refund_amount || 0),
    projectedReceivableTarget: Number(preview?.projected_receivable_target || 0),
    projectedReceivableAdjustment: Number(preview?.projected_receivable_adjustment || 0),
    projectedReceivableDirection: preview?.projected_receivable_direction || null,
    warnings: preview?.warnings || [],
});

/** Số tiền khách cần nộp thêm sau khi trừ ưu đãi và các nguồn tiền nội bộ. */
export const calculateReceiptExternalAmount = ({
    productAmount = 0,
    serviceAmount = 0,
    specialDiscount = 0,
    balanceAmount = 0,
    depositAmount = 0,
} = {}) => {
    const product = Math.max(0, Number(productAmount) || 0);
    const serviceAfterDiscount = Math.max(0,
        (Number(serviceAmount) || 0) - (Number(specialDiscount) || 0));
    const settlementAmount = product + serviceAfterDiscount;
    const internalAmount = Math.min(settlementAmount,
        Math.max(0, Number(balanceAmount) || 0) + Math.max(0, Number(depositAmount) || 0));
    return Math.max(0, settlementAmount - internalAmount);
};

/** Giữ cùng quy tắc chia tiền mới theo tỷ trọng Sản phẩm/Dịch vụ như backend. */
export const calculateReceiptSettlement = ({
    externalAmount = 0,
    productAmount = 0,
    serviceAmount = 0,
    specialDiscount = 0,
    balanceAmount = 0,
    depositAmount = 0,
} = {}) => {
    const external = Math.max(0, Math.round(Number(externalAmount) || 0));
    const product = Math.max(0, Math.round(Number(productAmount) || 0));
    const service = Math.max(0, Math.round(Number(serviceAmount) || 0));
    const discount = Math.max(0, Math.round(Number(specialDiscount) || 0));
    const balance = Math.max(0, Math.round(Number(balanceAmount) || 0));
    const deposit = Math.max(0, Math.round(Number(depositAmount) || 0));
    const outstanding = product + service;
    const allocatableExternal = Math.min(external, outstanding);
    let externalProduct = outstanding > 0
        ? Math.min(product, Math.round(allocatableExternal * product / outstanding))
        : 0;
    let externalService = Math.min(service, Math.round(allocatableExternal - externalProduct));
    let remaining = Math.round(allocatableExternal - externalProduct - externalService);
    const productTake = Math.min(remaining, product - externalProduct);
    externalProduct += productTake;
    remaining -= productTake;
    externalService += Math.min(remaining, service - externalService);

    const serviceNeed = Math.max(0, service - discount);
    const remainingProductNeed = Math.max(0, product - externalProduct);
    const remainingServiceNeed = Math.max(0, serviceNeed - externalService);
    const internalLimit = Math.max(0, remainingProductNeed + remainingServiceNeed);
    const internalAmount = Math.min(internalLimit, balance + deposit);
    let internalProduct = internalLimit > 0
        ? Math.min(remainingProductNeed, Math.round(internalAmount * remainingProductNeed / internalLimit))
        : 0;
    let internalService = Math.min(remainingServiceNeed, internalAmount - internalProduct);
    let internalRemaining = internalAmount - internalProduct - internalService;
    const internalProductTake = Math.min(internalRemaining, remainingProductNeed - internalProduct);
    internalProduct += internalProductTake;
    internalRemaining -= internalProductTake;
    internalService += Math.min(internalRemaining, remainingServiceNeed - internalService);
    const debtReduction = Math.min(serviceNeed, externalService + internalService);
    return {
        settlementTotal: external + balance + deposit,
        externalAmount: external,
        externalProductAmount: externalProduct,
        externalServiceAmount: externalService,
        balanceAmount: balance,
        depositAmount: deposit,
        internalLimit,
        debtReductionAmount: debtReduction,
        productSettlementAmount: externalProduct + internalProduct,
        serviceSettlementAmount: externalService + internalService,
        unallocatedExternalAmount: Math.max(0, external - externalProduct - externalService),
    };
};

/** Dòng số tiền 0 chỉ là placeholder giao diện, không phải hình thức thu thực tế. */
export const buildReceiptPaymentBreakdowns = rows => (Array.isArray(rows) ? rows : [])
    .filter(row => Number(row.amount || 0) > 0)
    .map(({ key, ...row }) => ({
        ...row,
        amount: Number(row.amount || 0),
    }));

/** Chỉ hiển thị hướng dẫn thanh toán nội bộ khi khách thực sự có số dư hoặc tiền cọc. */
export const hasReceiptInternalPaymentSource = ({ balanceAvailable = 0, activeDeposits = [] } = {}) => (
    Number(balanceAvailable || 0) > 0
    || (Array.isArray(activeDeposits) ? activeDeposits : [])
        .some(deposit => Number(deposit.current_balance || 0) > 0)
);

/** Gộp số dư hiện tại với phần đang dùng trên chính phiếu để edit được cả khoản cọc đã dùng hết. */
export const mergeEditableDeposits = (activeDeposits = [], currentApplication = {}) => {
    const byId = new Map((activeDeposits || []).map(deposit => [Number(deposit.id), {
        ...deposit,
        current_balance: Number(deposit.current_balance || 0),
    }]));
    (currentApplication.deposits || []).forEach(deposit => {
        const id = Number(deposit.id);
        const active = byId.get(id) || {};
        const currentBalance = Number(active.current_balance ?? deposit.current_balance ?? 0);
        byId.set(id, {
            ...deposit,
            ...active,
            id,
            actual_current_balance: currentBalance,
            current_balance: currentBalance + Number(deposit.applied_amount || 0),
            applied_amount: Number(deposit.applied_amount || 0),
        });
    });
    return Array.from(byId.values()).filter(deposit => Number(deposit.current_balance || 0) > 0);
};

/** Tách số dư ledger hiện tại khỏi hạn mức có thể chọn lại khi sửa phiếu. */
export const calculateEditableBalance = ({ available = 0, applied = 0, requested = 0 } = {}) => {
    const actualAvailable = Math.max(0, Math.round(Number(available) || 0));
    const currentApplied = Math.max(0, Math.round(Number(applied) || 0));
    const requestedAmount = Math.max(0, Math.round(Number(requested) || 0));
    return {
        actualAvailable,
        editableLimit: actualAvailable + currentApplied,
        projectedRemaining: Math.max(0, actualAvailable + currentApplied - requestedAmount),
    };
};

export const getReceiptItemValidationMessage = (items = []) => {
    if (!Array.isArray(items) || items.length === 0) {
        return 'Phiếu thu thanh toán phải có ít nhất một dịch vụ hoặc sản phẩm hợp lệ.';
    }

    const invalidIndex = items.findIndex(item => {
        if (!item || typeof item !== 'object') return true;

        const type = String(item.type || '').trim();
        const customerServiceId = Number(item.customer_service_id || 0);
        if (type === 'Dịch vụ') return !Number.isInteger(customerServiceId) || customerServiceId <= 0;

        if (type !== 'Sản phẩm') return true;
        const productSaleId = Number(item.product_sale_id || 0);
        if (Number.isInteger(productSaleId) && productSaleId > 0) return false;

        const productId = Number(item.origin_id || 0);
        const quantity = Number(item.quantity);
        const price = Number(item.price);
        return !Number.isInteger(productId) || productId <= 0
            || !String(item.name || '').trim()
            || !Number.isInteger(quantity) || quantity <= 0
            || !Number.isFinite(price) || price < 0;
    });

    return invalidIndex >= 0
        ? `Dòng nội dung số ${invalidIndex + 1} không hợp lệ. Vui lòng chọn lại dịch vụ hoặc sản phẩm.`
        : null;
};

export const buildReceiptMutationControlFields = (preview, mutationKey) => {
    if (!preview?.financial_changed) return {};
    return {
        mutation_key: mutationKey,
        ...(preview.deposit_reconciliation_required ? {
            deposit_state_fingerprint: preview.deposit_state_fingerprint,
        } : {}),
        ...(preview.balance_reconciliation_required ? {
            balance_state_fingerprint: preview.balance_state_fingerprint,
        } : {}),
        ...(preview.requires_confirmation ? { confirmation_token: preview.confirmation_token } : {}),
    };
};

export const getAvailableCustomerServices = (services = [], items = []) => {
    const existingIds = new Set((items || [])
        .filter(item => item.type === 'Dịch vụ' && item.customer_service_id)
        .map(item => Number(item.customer_service_id)));
    const seenIds = new Set();
    return (services || []).filter(service => {
        const id = Number(service?.id || 0);
        const isAvailable = id > 0
            && Number(service?.outstanding_amount || 0) > 0
            && !existingIds.has(id)
            && !seenIds.has(id);
        if (isAvailable) seenIds.add(id);
        return isAvailable;
    });
};

export const appendCustomerServices = (
    items = [],
    services = [],
    selectedIds = [],
    { allocationScope = 'CURRENT_TOTAL' } = {}
) => {
    const selected = new Set((selectedIds || []).map(Number));
    const additions = getAvailableCustomerServices(services, items)
        .filter(service => selected.has(Number(service.id)))
        .map(service => ({
            id: service.id,
            key: `Dịch vụ-${service.id}`,
            origin_id: service.service_id,
            name: service.service?.name || service.service_name || 'Dịch vụ',
            type: 'Dịch vụ',
            price: Number(service.sale_price || 0),
            quantity: Number(service.quantity || 1),
            amount: Number(service.outstanding_amount || 0),
            outstanding_amount: Number(service.outstanding_amount || 0),
            allocated_amount: allocationScope === 'RECEIPT'
                ? 0
                : Number(service.allocated_amount || 0),
            effective_allocated_amount: allocationScope === 'RECEIPT' ? 0 : undefined,
            refunded_allocated_amount: allocationScope === 'RECEIPT' ? 0 : undefined,
            allocation_scope: allocationScope,
            line_total: Number(service.sale_price || 0) * Number(service.quantity || 1),
            customer_service_id: service.id,
            selected_at: service.selected_at || null,
        }));
    return [...(items || []), ...additions];
};
